"use client";

import { useQuery } from "@apollo/client/react";
import Image from "next/image";

import VerifiedIcon from "@/assets/verified.gif";
import { AccountStatusByPublicKeyDocument } from "@/graphql/codegen/graphql";
import { isDesoFoundationToken, isWrappedAsset } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";

import { InfoTooltip } from "./info-tooltip";

type UserVerifiedIconProps = {
  account?: PartialAccount;
  isVerified?: PartialAccount["isVerified"];
  publicKey?: string;
  width?: number;
  height?: number;
  className?: string;
  tooltipText?: string;
};

export const UserVerifiedIcon = ({
  account,
  isVerified = account?.isVerified,
  width = 32,
  height = 32,
  publicKey = account?.publicKey,
  className,
  tooltipText = "Verified",
}: UserVerifiedIconProps) => {
  if (!publicKey) {
    throw new Error(
      "publicKey is required, either by setting it directly or from an `account` object.",
    );
  }

  const alwaysVerifiedToken =
    isWrappedAsset(publicKey) || isDesoFoundationToken(publicKey);
  const shouldFetchVerification =
    !alwaysVerifiedToken && isVerified === undefined;

  const { data } = useQuery(AccountStatusByPublicKeyDocument, {
    variables: { publicKey },
    skip: !shouldFetchVerification,
  });

  const resolvedIsVerified =
    isVerified ?? data?.accountByPublicKey?.isVerified ?? false;

  if (!alwaysVerifiedToken && !resolvedIsVerified) {
    return null;
  }

  return (
    <InfoTooltip
      text={<span className="text-xs text-muted">{tooltipText}</span>}
    >
      <Image
        src={VerifiedIcon}
        alt={tooltipText}
        {...{ width, height }}
        className={cn("max-h-max", className)}
      />
    </InfoTooltip>
  );
};
